<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header Section -->
    <div class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              class="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              @click="$router.push('/packages')"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>Back to Packages</span>
            </button>
            <div class="flex items-center space-x-2">
              <div
                class="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-blue-600 dark:text-blue-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ packageName }}
              </h1>
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <button
              class="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6">
      <!-- Overview Card -->
      <div
        v-if="packageDetails"
        class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6"
      >
        <div class="p-6">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            📊 Package Overview
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Latest Version</div>
              <div class="flex items-center mt-1">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 font-mono"
                >
                  {{ packageDetails.latestVersion }}
                </span>
              </div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Total Workloads</div>
              <div class="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {{ packageDetails.totalWorkloads }}
              </div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Environments</div>
              <div class="flex flex-wrap gap-1 mt-1">
                <span
                  v-for="env in packageDetails.environments"
                  :key="env"
                  :class="[
                    'inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium',
                    env === 'interop'
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                      : env === 'staging'
                        ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  ]"
                >
                  {{ env }}
                </span>
              </div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Last Updated</div>
              <div class="text-sm text-gray-900 dark:text-white mt-1">
                {{ formatDate(packageDetails.lastUpdated) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Workloads Table -->
      <div
        class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
      >
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              🔧 Workload Deployments
            </h3>

            <!-- Version Filter -->
            <div class="flex items-center space-x-2">
              <label class="text-sm text-gray-600 dark:text-gray-400">Filter by Version:</label>
              <select
                v-model="selectedVersionFilter"
                class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Versions</option>
                <option
                  v-for="ver in availableVersions"
                  :key="ver"
                  :value="ver"
                >
                  {{ ver }}
                </option>
              </select>
              <button
                v-if="selectedVersionFilter"
                class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                @click="clearVersionFilter"
              >
                Clear
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div
            v-if="loading"
            class="flex items-center justify-center py-8"
          >
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span class="ml-3 text-gray-600 dark:text-gray-400">Loading workloads...</span>
          </div>

          <!-- Table -->
          <DataTable
            v-else
            :data="filteredWorkloads"
            :columns="tableColumns"
            :config="tableConfig"
          >
            <template #workloadName="{ item }">
              <button
                class="flex items-center space-x-2 hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded transition-colors"
                @click="openWorkloadDetails(item)"
              >
                <div
                  class="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded flex items-center justify-center flex-shrink-0"
                >
                  <svg
                    class="w-3 h-3 text-blue-600 dark:text-blue-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <span class="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                  {{ item.workloadName }}
                </span>
              </button>
            </template>

            <template #versions="{ item }">
              <div class="flex flex-wrap gap-1.5 py-1">
                <span
                  v-for="ver in item.versions"
                  :key="ver"
                  :class="[
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-mono',
                    ver === item.latestVersion
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  ]"
                >
                  {{ ver }}
                  <span
                    v-if="ver === item.latestVersion"
                    class="ml-1 text-[10px] text-green-600 dark:text-green-400 font-semibold"
                  >
                    ✓
                  </span>
                </span>
              </div>
            </template>

            <template #environment="{ item }">
              <span
                :class="[
                  'inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium',
                  item.environment === 'interop'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                    : item.environment === 'staging'
                      ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                ]"
              >
                {{ item.environment }}
              </span>
            </template>

            <template #gitRepo="{ item }">
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-900 dark:text-white font-mono">
                  {{ item.gitRepo }}
                </span>
                <button
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Copy repository name"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DataTable from '../../components/shared/DataTable/index.vue'
import { packageVersionService } from '../../services/package-version'
import type { PackageDetail } from '../../types/package-version'
import { logger } from '../../utils/logger'

const route = useRoute()
const router = useRouter()

// Data
const packageDetails = ref<PackageDetail | null>(null)
const loading = ref(false)
const selectedVersionFilter = ref<string>('')

// Get route params
const packageName = computed(() => route.params.packageName as string)
const version = computed(() => route.params.version as string | undefined)

// Group workloads by workloadName-gitRepo-environment
interface GroupedWorkload {
  workloadName: string
  versions: string[]
  environment: string
  gitRepo: string
  hasLatest: boolean
  latestVersion: string
}

const workloads = computed<GroupedWorkload[]>(() => {
  if (!packageDetails.value) return []

  const latestVersion = packageDetails.value.latestVersion
  const groupMap = new Map<string, GroupedWorkload>()

  // Iterate through all versions and group workloads
  Object.entries(packageDetails.value.versions).forEach(([versionKey, versionData]) => {
    versionData.workloads.forEach((workload) => {
      // Create unique key for grouping: workloadName-gitRepo-environment
      const groupKey = `${workload.name}-${workload.gitRepo}-${workload.environment}`

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          workloadName: workload.name,
          versions: [],
          environment: workload.environment,
          gitRepo: workload.gitRepo,
          hasLatest: false,
          latestVersion
        })
      }

      const group = groupMap.get(groupKey)
      if (group && !group.versions.includes(versionKey)) {
        group.versions.push(versionKey)
      }
      if (group && versionKey === latestVersion) {
        group.hasLatest = true
      }
    })
  })

  // Sort versions in each group lexicographically
  const result = Array.from(groupMap.values())
  result.forEach((workload) => {
    workload.versions.sort()
  })

  return result
})

// Get all available versions sorted
const availableVersions = computed<string[]>(() => {
  if (!packageDetails.value) return []
  return Object.keys(packageDetails.value.versions).sort()
})

// Filter workloads based on selected version
const filteredWorkloads = computed<GroupedWorkload[]>(() => {
  if (!selectedVersionFilter.value) {
    return workloads.value
  }

  // Filter workloads to only show those that include the selected version
  return workloads.value.filter((workload) =>
    workload.versions.includes(selectedVersionFilter.value)
  )
})

// Table Configuration
const tableColumns = [
  { field: 'workloadName', label: 'Workload Name', sortable: true, searchable: true },
  { field: 'versions', label: 'Versions', sortable: false, searchable: false },
  { field: 'environment', label: 'Environment', sortable: true, searchable: true },
  { field: 'gitRepo', label: 'Git Repository', sortable: true, searchable: true }
]

const tableConfig = {
  enableSearch: true,
  searchPlaceholder: 'Search workloads, versions, environments...',
  enableSort: true,
  enablePagination: true,
  itemsPerPage: 20,
  enableFilter: false
}

// Methods
const loadPackageDetails = async () => {
  try {
    loading.value = true
    // Always fetch ALL versions from backend (no version parameter)
    const request = {
      packageName: packageName.value
    }

    logger.log('Loading package details for:', request)
    const response = await packageVersionService.getPackageDetails(request)
    packageDetails.value = response
    logger.log('Package details loaded:', response)

    // Pre-fill version filter if version was provided in route
    if (version.value) {
      selectedVersionFilter.value = version.value
    }
  } catch (error) {
    logger.error('Failed to load package details:', error)
  } finally {
    loading.value = false
  }
}

const clearVersionFilter = () => {
  selectedVersionFilter.value = ''
}

const openWorkloadDetails = (item: GroupedWorkload) => {
  // Navigate to workload details page
  router.push(`/workload/${encodeURIComponent(item.gitRepo)}/${encodeURIComponent(item.workloadName)}/${encodeURIComponent(item.environment)}`)
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Lifecycle
onMounted(() => {
  loadPackageDetails()
})
</script>

<style scoped>
/* Custom styles for the package details view */
</style>
